import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Drawer,
  FormGroup,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableHeadCellFilterContainer,
  useMaterialReactTable
} from "material-react-table";
import { Helmet } from "react-helmet-async";
import { createPortal } from "react-dom";
import { Global, css } from "@emotion/react";
import Iconify from "../../minimals/components/iconify";
import useResponsive from "../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../components/layouts/dashboard/HeaderSlotContext";
import { useSettingsContext } from "../contexts/SettingsContext";
import { User, UserContext } from "../contexts/UserContext";
import InternalUserAutocomplete from "../shared/InternalUserAutocomplete";
import { InternalUser } from "../types/app";
import Dashboard from "./Dashboard";
import { tmeOrderColumns } from "./tmeOrderColumns";
import { TMEOrderDetails } from "./types";
import { orderColumnsByList } from "../utils/materialReactTableUtils";
import { getConfiguration } from "./configurations";
import useLocalStorage from "../../minimals/hooks/useLocalStorage";
import useAxiosCancel from "../utils/useAxiosCancel";
import { StyledSwitch } from "./StyledSwitch";
import axios from "axios";
import MobileOrderCards from "./MobileOrderCards";
import useKeyboardInset from "../../hooks/useKeyboardInset";

const inFlightOrderUrls = new Set<string>();

export default function TMEOrders() {
  const { user } = useContext(UserContext);
  const { slotEl } = useContext(HeaderSlotContext);
  const { fetchData, isLoading } = useAxiosCancel();
  const isMobile = useResponsive("down", "md");

  const config = getConfiguration(user?.roles);
  const orderedColumns = orderColumnsByList(tmeOrderColumns, config.columnOrderOptions);

  const settings = useSettingsContext();
  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([]);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [orders, setOrders] = useState<TMEOrderDetails[]>([]);
  const [userReportPermissions, setUserReportPermissions] = useState<string[]>([]);
  const [dashboardInternalUserIds, setDashboardInternalUserIds] = useState<number[]>([]);
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [isShowAssignedToMyTeam, setIsShowAssignedToMyTeam] = useLocalStorage<boolean>("isShowAssignedToMyTeam", true);
  const [isUserManagerOrTME, setIsUserManagerOrTME] = useState(false);

  const removeDuplicates = (users: InternalUser[]) => {
    const seenIds = new Set<number>();
    return users.filter((user) => {
      if (seenIds.has(user.id)) {
        return false;
      } else {
        seenIds.add(user.id);
        return true;
      }
    });
  };

  const handleShowTeamToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsShowAssignedToMyTeam(event.target.checked);
    if (user) {
      getAndSetInternalUsers(user, event.target.checked);
    }
  };

  const setUsersAndGetOrders = (internalUsers: InternalUser[], isShowAssignedToMyTeam: boolean) => {
    setInternalUsers(internalUsers);
    getAndSetDashboardInternalUserIds(internalUsers);
    getAndSetOrders(internalUsers, isShowAssignedToMyTeam);
  };

  const getAndSetDashboardInternalUserIds = async (internalUsers: InternalUser[]) => {
    if (internalUsers.length === 0) {
      setDashboardInternalUserIds([]);
      return;
    }

    const queryParams = internalUsers.map((u) => `internalUserIds=${u.id}`).join("&");
    const { data } = await axios.get<number[]>(`/api/orders/getAssignableInternalUserIds?${queryParams}`);
    setDashboardInternalUserIds(data);
  };

  const getAndSetInternalUsers = async (user: User, isShowAssignedToMyTeam: boolean) => {
    const id = user?.id;
    if (!id) return;

    const url = isShowAssignedToMyTeam
      ? `/api/clientphonebook/internalusers/getInternalUserAndTeamMembers?id=${id}`
      : `/api/clientphonebook/internalusers/getInternalUserById?id=${id}`;

    const { data } = await axios.get<InternalUser[] | InternalUser | null>(url);

    if (data) {
      const usersArray = Array.isArray(data) ? data : [data];
      const updatedUsers = isShowAssignedToMyTeam ? removeDuplicates(usersArray) : usersArray;
      setInternalUsers(updatedUsers);
      await Promise.all([
        getAndSetDashboardInternalUserIds(updatedUsers),
        getAndSetOrders(updatedUsers, isShowAssignedToMyTeam)
      ]);
    }
  };

  const getUserReportPermissions = async (id: number) => {
    const { data } = await axios.get<string[]>(
      `/api/clientphonebook/dashboardReportPermissions/GetReportPermissionsForInternalUser?id=${id}`
    );
    setUserReportPermissions(data);
  };

  const getIfUserIsManagerOrTME = async (id: number) => {
    if (!id) return;
    const { data } = await axios.get<boolean>(
      `/api/clientphonebook/internalusers/getIsInternalUserManagerOrTME?id=${id}`
    );

    setIsUserManagerOrTME(data);
  };

  const getAndSetOrders = async (internalUsers: InternalUser[], isShowAssignedToMyTeam: boolean) => {
    if (internalUsers.length === 0) {
      setOrders([]);
      return;
    }
    const baseUrl = `/api/orders/getOrderDetails?isShowUnassigned=${isShowAssignedToMyTeam}`;
    const queryParams = internalUsers.length > 0 ? internalUsers.map((u) => `internalUserIds=${u.id}`).join("&") : "";
    const url = queryParams ? `${baseUrl}&${queryParams}` : baseUrl;
    if (inFlightOrderUrls.has(url)) {
      return;
    }

    inFlightOrderUrls.add(url);
    try {
      const data: TMEOrderDetails[] = await fetchData(url);
      if (data) {
        data.sort((a, b) => +a.number - +b.number);
        setOrders(data);
      }
    } finally {
      inFlightOrderUrls.delete(url);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    getIfUserIsManagerOrTME(user.id);
    getAndSetInternalUsers(user, isShowAssignedToMyTeam);
    getUserReportPermissions(user.id);
  }, [user?.id]);

  return (
    <>
      <Helmet>
        <title>TitleQ Orders</title>
      </Helmet>

      {isMobile && (
        <Global
          styles={css`
            main {
              /* The title lives in the 64px site header now, and the cards wrapper
                 already reserves clearance for the floating bar. Main's default 72px
                 on each side stacked on top of that made short lists scroll. */
              padding-top: 64px !important;
              padding-bottom: 0 !important;
            }
          `}
        />
      )}

      {isMobile && slotEl && !user
        ? createPortal(
            <Typography
              noWrap
              sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}
            >
              Orders
            </Typography>,
            slotEl
          )
        : null}

      <Container maxWidth={settings.themeStretch ? false : "xl"} sx={{ borderRadius: 8 }}>
        <Stack
          direction="row"
          spacing={isUserManagerOrTME ? 1 : 2}
          marginBottom={{
            xs: isUserManagerOrTME || dashboardInternalUserIds.length > 0 ? 1 : 0,
            md: isUserManagerOrTME ? 1 : 3
          }}
          alignItems="center"
          visibility={isFullScreen ? "hidden" : "visible"}
          sx={isMobile ? { flexWrap: "wrap", rowGap: 1 } : undefined}
        >
          {!isMobile && <Typography variant="h4">Orders</Typography>}
          {isUserManagerOrTME && (
            <InternalUserAutocomplete
              multiple
              value={internalUsers}
              onChange={(newValue) => {
                setIsShowAssignedToMyTeam(false);
                if (newValue === null) {
                  setUsersAndGetOrders([], false);
                } else if (Array.isArray(newValue)) {
                  setUsersAndGetOrders(newValue, false);
                }
              }}
              sx={{ width: isMobile ? "100%" : 300 }}
            />
          )}
          {dashboardInternalUserIds.length > 0 && (
            <Dashboard internalUserIds={dashboardInternalUserIds} permissions={userReportPermissions || []} />
          )}
        </Stack>
        {isUserManagerOrTME && (
          <FormGroup sx={{ marginBottom: 4 }}>
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">Mine</Typography>
              <StyledSwitch
                checked={isShowAssignedToMyTeam}
                size="small"
                onChange={handleShowTeamToggleChange}
                inputProps={{ "aria-label": "controlled" }}
              />
              <Typography variant="body2">My Team's</Typography>
            </Stack>
          </FormGroup>
        )}
        {/* The table is only created once the user has loaded: its initialState
            carries the role-based filters and sort, and MRT reads initialState once.
            Before this lived in a hook here, `user &&` on the element guaranteed that. */}
        {user ? (
          <OrdersView
            orders={orders}
            columns={orderedColumns}
            config={config}
            isLoading={isLoading}
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
            enableOrdering={enableOrdering}
            setEnableOrdering={setEnableOrdering}
            isMobile={isMobile}
          />
        ) : (
          !isMobile && <Card sx={desktopCardSx} />
        )}
      </Container>
    </>
  );
}

// Unchanged from the original Card wrapper around the desktop table.
const desktopCardSx = {
  paddingRight: 4,
  paddingTop: 2,
  "& .MuiPaper-root, & .MuiCard-root": {
    boxShadow: "none !important"
  }
};

type OrdersViewProps = {
  orders: TMEOrderDetails[];
  columns: MRT_ColumnDef<TMEOrderDetails>[];
  config: ReturnType<typeof getConfiguration>;
  isLoading: boolean;
  isFullScreen: boolean;
  // React setters, not (value) => void: MRT hands onIsFullScreenChange an Updater,
  // which may be a function of the previous value.
  setIsFullScreen: Dispatch<SetStateAction<boolean>>;
  enableOrdering: boolean;
  setEnableOrdering: Dispatch<SetStateAction<boolean>>;
  isMobile: boolean;
};

/**
 * One table instance feeding both layouts: desktop renders it exactly as before,
 * mobile reads its filtered and sorted rows as cards. Holding the instance here (not
 * per layout) means crossing the breakpoint doesn't reset anyone's filters.
 */
function OrdersView({
  orders,
  columns,
  config,
  isLoading,
  isFullScreen,
  setIsFullScreen,
  enableOrdering,
  setEnableOrdering,
  isMobile
}: OrdersViewProps) {
  const keyboardInset = useKeyboardInset(isMobile);
  const { slotEl } = useContext(HeaderSlotContext);

  // Options are exactly the props the <MaterialReactTable> element used to receive.
  const table = useMaterialReactTable({
    enableStickyHeader: true,
    onIsFullScreenChange: (value) => {
      setIsFullScreen(value);
    },
    columns,
    data: orders,
    enableGlobalFilter: true,
    // Seeds MRT's search mode at mount; the effect below follows later resizes. undefined
    // leaves desktop on MRT's default, exactly as before.
    globalFilterFn: isMobile ? "contains" : undefined,
    enableFacetedValues: true,
    enableColumnOrdering: enableOrdering,
    enableMultiSort: true,
    initialState: {
      density: "compact",
      pagination: { pageIndex: 0, pageSize: 30 },
      columnFilters: config.filterOptions,
      sorting: config.sortingOptions
    },
    state: {
      showSkeletons: isLoading,
      showColumnFilters: true,
      isFullScreen
    },
    muiTableProps: {
      sx: {
        paddingLeft: 4
      }
    },
    muiTableBodyProps: {
      sx: {
        boxShadow: "none",
        "& .MuiTableRow-root:hover .MuiTableCell-root": {
          backgroundColor: "inherit !important"
        }
      }
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "end" }}>
        <Tooltip title="Column ordering">
          <IconButton onClick={() => setEnableOrdering(!enableOrdering)} aria-label="toggle column ordering">
            <Iconify icon="mdi:reorder-vertical" />
          </IconButton>
        </Tooltip>
      </Box>
    )
  });

  const { globalFilter, columnFilters } = table.getState();
  // Plain case-insensitive substring search on mobile: MRT's default "fuzzy" matches
  // letters in order anywhere in a field, so "patel" returned an order with no Patel
  // on it. MRT holds the search function in internal state seeded once from the
  // option above (the `state` prop is ignored for it), so a resize across the
  // breakpoint goes through its setter. TanStack caches filtered rows by filter values
  // and search text, not by function, so after a resize the new mode applies from the
  // next keystroke. Setting "fuzzy" on desktop is a no-op: it's already the value.
  useEffect(() => {
    table.setGlobalFilterFn(isMobile ? "contains" : "fuzzy");
  }, [isMobile]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const filterableHeaders = table.getLeafHeaders().filter((header) => header.column.getCanFilter());

  if (!isMobile) {
    return (
      <Card sx={desktopCardSx}>
        <MaterialReactTable table={table} />
      </Card>
    );
  }

  // MRT pads the row model with 20 null placeholders while loading with no data, so
  // the count only means something once real orders exist.
  const showCount = !(isLoading && orders.length === 0);
  const orderCount = table.getPrePaginationRowModel().rows.length;

  return (
    <>
      {slotEl
        ? createPortal(
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: 1, minWidth: 0, gap: 1 }}
            >
              <Typography
                noWrap
                sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}
              >
                Orders
              </Typography>
              {showCount && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                  {orderCount} {orderCount === 1 ? "order" : "orders"}
                </Typography>
              )}
            </Stack>,
            slotEl
          )
        : null}

      {/* Clearance for the search pill only (44 tall + 12 offset + breathing room) —
          the gradient fade above it is meant to overlap content. */}
      <Box sx={{ mx: -2, pb: "calc(72px + env(safe-area-inset-bottom))" }}>
        <MobileOrderCards table={table} isLoading={isLoading} orderCount={orders.length} />
      </Box>

      {/* Same floating bar as /documents and the phonebook. It searches through the
          table's global filter, so it narrows exactly what the cards are reading. */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.drawer - 1,
          px: 1.5,
          pt: keyboardInset > 0 ? 1.5 : 4,
          pb: keyboardInset > 0 ? 1 : "calc(12px + env(safe-area-inset-bottom))",
          transform: `translateY(-${keyboardInset}px)`,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          backgroundColor: keyboardInset > 0 ? "background.paper" : undefined,
          boxShadow: keyboardInset > 0 ? "0 -6px 18px rgba(22, 28, 36, 0.14)" : undefined,
          background:
            keyboardInset > 0
              ? undefined
              : (theme) =>
                  `linear-gradient(to top, ${theme.palette.background.default} 55%, ${alpha(
                    theme.palette.background.default,
                    0
                  )})`
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pointerEvents: "auto", width: 1, maxWidth: 560 }}>
          <Badge badgeContent={columnFilters.length} color="primary" overlap="circular" sx={{ flexShrink: 0 }}>
            <IconButton
              aria-label={`Filters${columnFilters.length ? ` (${columnFilters.length} active)` : ""}`}
              onClick={() => setIsFilterDrawerOpen(true)}
              sx={{
                width: 44,
                height: 44,
                color: "primary.dark",
                backgroundColor: "background.paper",
                border: "1px solid rgba(145, 158, 171, 0.48)",
                boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
                "&:hover": { backgroundColor: "background.paper" }
              }}
            >
              <Iconify icon="ic:round-filter-list" width={22} />
            </IconButton>
          </Badge>
          <TextField
            fullWidth
            placeholder="Search orders"
            value={globalFilter ?? ""}
            onChange={(event) => table.setGlobalFilter(event.target.value || undefined)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={20} />
                </InputAdornment>
              ),
              endAdornment: globalFilter ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="Clear search"
                    onClick={() => table.setGlobalFilter(undefined)}
                    sx={{ color: "text.disabled" }}
                  >
                    <Iconify icon="eva:close-circle-fill" width={18} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 44,
                borderRadius: 999,
                backgroundColor: "background.paper",
                boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)"
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(145, 158, 171, 0.48)"
              },
              // 16px floor: below that iOS zooms the page on focus and never zooms back.
              "& .MuiInputBase-input": { fontSize: 16 }
            }}
          />
        </Stack>
      </Box>

      {/* The table's own header filters, rendered by the same MRT component the desktop
          header uses — custom date-range/status/recorded-docs filters included — so
          they read and write the same column-filter state the cards are built from. */}
      {isFilterDrawerOpen && (
        <Global
          styles={css`
            /* MUI gives autocomplete options a 48px min-height on touch screens. The
               filter dropdowns are short lists, so tighter rows show them all at once. */
            .MuiAutocomplete-popper .MuiAutocomplete-option {
              min-height: 40px;
              padding-top: 6px;
              padding-bottom: 6px;
            }
          `}
        />
      )}

      <Drawer
        anchor="bottom"
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        PaperProps={{
          sx: { maxHeight: "85vh", borderRadius: "20px 20px 0 0", pb: "env(safe-area-inset-bottom)" }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>Filters</Typography>
            {/* Updates live as filters change, so the effect is visible without closing. */}
            <Typography variant="caption" color="text.secondary" noWrap>
              {table.getPrePaginationRowModel().rows.length} of {orders.length} orders
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Button
              size="small"
              onClick={() => {
                // true = clear to empty. Without it, reset restores the role's default
                // filters (status In process/Transferred), which read as "nothing cleared".
                table.resetColumnFilters(true);
                // SingleDateRangeFilter copies the filter value into local state once;
                // remounting is what makes the date field show the reset value.
                setFilterResetKey((key) => key + 1);
              }}
            >
              Clear all
            </Button>
            <IconButton size="small" aria-label="Close filters" onClick={() => setIsFilterDrawerOpen(false)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          spacing={2}
          sx={{
            p: 2,
            overflowY: "auto",
            overscrollBehavior: "contain",
            // Header filters are sized to their column; here they take the full width.
            "& .MuiFormControl-root, & .MuiTextField-root, & .MuiAutocomplete-root": {
              width: "100% !important",
              minWidth: "0 !important"
            },
            // 16px floor so iOS doesn't zoom on focus.
            "& .MuiInputBase-input": { fontSize: 16 },
            "& .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.500"
            }
          }}
        >
          {filterableHeaders.map((header) => (
            <Box
              key={`${header.id}-${filterResetKey}`}
              // The date range field opens a picker on mobile rather than taking typed
              // input, so it's exempt from the 16px anti-zoom floor.
              sx={
                header.id === "settlementDate"
                  ? { "& .MuiInputBase-input": { fontSize: "14px !important" } }
                  : undefined
              }
            >
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 0.5, fontWeight: 700, color: "text.secondary" }}
              >
                {header.column.columnDef.header}
              </Typography>
              <MRT_TableHeadCellFilterContainer header={header} table={table} />
            </Box>
          ))}
        </Stack>
      </Drawer>
    </>
  );
}
